<template>
  <div class="homeworks-container">
    <div class="content-header">
      <div class="header-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>
      </div>
      <h1>Házifeladatok</h1>
      <p>{{ isTeacher && !isAdmin ? 'Hozzárendelt csoportok házifeladatai' : 'Csoportonkénti házifeladatok kezelése és szerkesztése' }}</p>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Adatok betöltése...</p>
    </div>

    <!-- Homework table -->
    <div v-if="!loading" class="homework-table-container">
      <div class="table-wrapper">
        <table class="modern-table">
          <thead>
            <tr>
              <th v-for="(events, grpName) in groupedEvents" :key="grpName" class="group-header">
                <div class="group-header-content">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  {{ grpName }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rowIndex in maxRowCount" :key="rowIndex">
              <td v-for="(events, grpName) in groupedEvents" :key="grpName" class="homework-cell">
                <div v-if="events[rowIndex - 1]" 
                     class="homework-block"
                     @dblclick="toggleEditHomework(events[rowIndex - 1])"
                     :style="getStatusBackgroundColor(events[rowIndex - 1].status.String)">
                  
                  <div class="homework-content">
                    <div class="homework-date">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      {{ formatDate(events[rowIndex - 1].set_date) }}
                    </div>
                    
                    <div class="homework-text">
                      <strong>Lecke:</strong> {{ events[rowIndex - 1].homework.String || 'N/A' }}
                    </div>
                  </div>
                  
                  <!-- Edit popup -->
                  <div v-if="events[rowIndex - 1].isEditing" class="edit-popup">
                    <div class="edit-popup-header">
                      <h4>Házifeladat szerkesztése</h4>
                      <button class="close-btn" @click.stop="closeEditHomework(events[rowIndex - 1])">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                    
                    <div class="edit-popup-body">
                      <textarea 
                        v-model="events[rowIndex - 1].newHomework" 
                        class="modern-textarea" 
                        placeholder="Új házifeladat megadása"
                        rows="4"
                      ></textarea>
                    </div>
                    
                    <div class="edit-popup-footer">
                      <button @click.stop="saveHomework(events[rowIndex - 1])" class="modern-btn modern-btn--success">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                        Mentés
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
  
  <script>
  import axios from 'axios';
  
  export default {
    data() {
      return {
        events: [], // Store fetched events
        groupedEvents: {}, // Store events grouped by `grp_name`
        loading: true, // Add a loading state to show loading message until data is fetched
        isAdmin: false,
        isTeacher: false,
        userId: null
      };
    },
    computed: {
      // Determine the maximum number of rows across all groups
      maxRowCount() {
        const rowCounts = Object.values(this.groupedEvents).map(events => events.length);
        return rowCounts.length ? Math.max(...rowCounts) : 0; // Max number of rows in any group
      }
    },
    mounted() {
      this.getIsAdmin();
      this.getIsTeacher();
      this.getUserId();
      this.getHomeworks();
    },
    methods: {
      getIsAdmin() {
        this.isAdmin = this.$isAdmin();
      },
      getIsTeacher() {
        this.isTeacher = this.$isTeacher();
      },
      getUserId() {
        this.userId = this.$getUserId();
      },
      // Toggle the visibility of the homework editing (text area)
      toggleEditHomework(event) {
        // Toggle the editing state for the clicked event
        event.isEditing = true;
      },
      
      // Close the homework editing popup manually when clicking the close button (X)
      closeEditHomework(event) {
        // Set `isEditing` to false to hide the text area
        event.isEditing = false;
      },
  
      // Format the date to show only the date portion (no time)
      formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString();  // This formats the date as "MM/DD/YYYY" (default format)
      },
  
      // Fetch the homework data from the backend
      async getHomeworks() {
        try {
          let response;
          
          // If user is a teacher, fetch only their assigned groups' homeworks
          if (this.isTeacher && !this.isAdmin) {
            const teacherId = this.userId;
            if (!teacherId) {
              throw new Error('Teacher ID not found');
            }
            
            // First get teacher's assigned groups
            const teacherResponse = await fetch(`${this.$BACKEND_HOST}/api/settings/teachers/groups?teacher_id=${teacherId}`);
            if (teacherResponse.ok) {
              const teacherData = await teacherResponse.json();
              const assignedGroupNames = teacherData.groups.map(g => g.name);
                
              // Get all homeworks and filter by assigned groups
              const allHomeworksResponse = await axios.get(this.$BACKEND_HOST + '/api/getallhomeworks');
              const allEvents = allHomeworksResponse.data.value;

              // Filter events to only show those from assigned groups
              this.events = allEvents.filter(event => assignedGroupNames.includes(event.grp_name));
            } else {
              this.events = [];
            }
          } else {
            // For admin users, fetch all homeworks
            response = await axios.get(this.$BACKEND_HOST + '/api/getallhomeworks');
            this.events = response.data.value;
          }
          
          this.loading = false; // Set loading to false after data is fetched

          // Sort the events by the 'set_date' in descending order (latest first)
          this.events.sort((a, b) => new Date(b.set_date) - new Date(a.set_date));

          // Organize events by their `grp_name` in the groupedEvents object
          this.events.forEach(event => {
            // If the group doesn't exist yet, create an empty array
            if (!this.groupedEvents[event.grp_name]) {
              this.groupedEvents[event.grp_name] = [];
            }
            console.log(event)
            // Check if the event id already exists in the group
            const eventExists = this.groupedEvents[event.grp_name].some(existingEvent => existingEvent.event_id === event.event_id);

            // If the event with the same id doesn't exist, push the new event
            if (!eventExists) {
              // Push the event to the appropriate group
              this.groupedEvents[event.grp_name].push({
                ...event,  // Copy the event data
                showDetails: false, // Initialize the showDetails flag for each event
                isEditing: false, // Flag to check if the homework is being edited
                newHomework: event.homework.String || '' // Store the new homework in a variable
              });
            }
          });
        } catch (error) {
          console.error('Error fetching data:', error);
          this.loading = false; // Stop loading even if there is an error
        }
      },
  
      // Function to return background color based on the status value
      getStatusBackgroundColor(status) {
        if (status === '10') {
          return { backgroundColor: 'lightgreen' }; // Light green for status 10
        } else if (status === '20') {
          return { backgroundColor: 'lightcoral' }; // Light red for status 20
        }
        return {}; // Default (no color change) if status is neither 10 nor 20
      },
  
      // Save the new homework via an API POST request
      saveHomework(event) {
        const updatedHomework = event.newHomework;
        // Send updated homework data to the backend
        axios.post(this.$BACKEND_HOST + '/api/updatehomework', {
          event_id: event.event_id,
          new_homework: updatedHomework
        })
        .then(response => {
          // If the homework is successfully saved, update the homework text in the event
          event.homework.String = updatedHomework;
          event.isEditing = false;  // Close the text area after saving
        })
        .catch(error => {
          console.error('Error saving homework:', error);
        });
      }
    }
  };
  </script>
  
  <style scoped>
  .homeworks-container {
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
    background: var(--color-background);
    min-height: 100vh;
  }

  .content-header {
    text-align: center;
    margin-bottom: 2rem;
    padding: 2rem;
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: var(--shadow);
    border: 1px solid var(--color-border);
  }

  .header-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    background: var(--button-primary);
    border-radius: 16px;
    margin-bottom: 1rem;
    color: white;
  }

  .content-header h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--color-heading);
    margin: 0 0 0.5rem 0;
  }

  .content-header p {
    color: var(--color-text-muted);
    font-size: 1.1rem;
    margin: 0;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid var(--color-border);
    border-top: 4px solid var(--button-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .homework-table-container {
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: var(--shadow);
    border: 1px solid var(--color-border);
    overflow: hidden;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .modern-table {
    width: 100%;
    border-collapse: collapse;
  }

  .group-header {
    background: var(--color-background-soft);
    padding: 1rem;
    text-align: center;
    border-bottom: 1px solid var(--color-border);
  }

  .group-header-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .group-header-content svg {
    color: var(--button-primary);
  }

  .homework-cell {
    padding: 1rem;
    vertical-align: top;
    border-bottom: 1px solid var(--color-border);
    border-right: 1px solid var(--color-border);
  }

  .homework-cell:last-child {
    border-right: none;
  }

  .homework-block {
    cursor: pointer;
    padding: 1rem;
    border-radius: 12px;
    border: 1px solid var(--color-border);
    background: var(--color-background-soft);
    transition: all 0.2s ease;
    position: relative;
  }

  .homework-block:hover {
    background: var(--color-background-mute);
    transform: translateY(-2px);
    box-shadow: var(--shadow);
  }

  .homework-content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .homework-date {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--color-text-muted);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .homework-date svg {
    color: var(--button-primary);
  }

  .homework-text {
    color: var(--color-text);
    line-height: 1.5;
  }

  .edit-popup {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: var(--shadow-lg);
    border: 2px solid var(--button-primary);
    z-index: 10;
    display: flex;
    flex-direction: column;
  }

  .edit-popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--color-border);
    background: var(--color-background-soft);
    border-radius: 10px 10px 0 0;
  }

  .edit-popup-header h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: var(--error);
    color: white;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-btn:hover {
    background: #dc2626;
    transform: scale(1.1);
  }

  .edit-popup-body {
    flex: 1;
    padding: 1.5rem;
  }

  .modern-textarea {
    width: 100%;
    padding: 0.875rem 1rem;
    border: 2px solid var(--color-border);
    border-radius: 8px;
    background: var(--card-bg);
    color: var(--color-text);
    font-size: 1rem;
    transition: all 0.2s ease;
    resize: vertical;
    min-height: 100px;
  }

  .modern-textarea:focus {
    outline: none;
    border-color: var(--button-primary);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .edit-popup-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--color-border);
    background: var(--color-background-soft);
    border-radius: 0 0 10px 10px;
  }

  .modern-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
  }

  .modern-btn--success {
    background: var(--success);
    color: white;
  }

  .modern-btn--success:hover {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: var(--shadow);
  }

  @media (max-width: 768px) {
    .homeworks-container {
      padding: 1rem;
    }

    .content-header {
      padding: 1.5rem;
    }

    .content-header h1 {
      font-size: 2rem;
    }

    .header-icon {
      width: 56px;
      height: 56px;
    }

    .homework-cell {
      padding: 0.75rem;
    }

    .homework-block {
      padding: 0.75rem;
    }

    .edit-popup-header,
    .edit-popup-body,
    .edit-popup-footer {
      padding: 1rem;
    }
  }
  </style>
  